import { useEffect, useMemo, useState } from 'react'
import type { ChatUser } from '../../../types/chat'
import { filterCallableContacts } from '../../../utils/bruno-config'
import { loadDirectory, useDirectory } from '../../../stores/directory-store'

export function AddPeoplePickerSheet({
  open,
  onClose,
  onAdd,
  excludeUserIds = [],
}: {
  open: boolean
  onClose: () => void
  onAdd: (users: ChatUser[]) => void
  excludeUserIds?: string[]
}) {
  const { contacts, loading, error, loadedAt, reload } = useDirectory()
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Load the cached directory once per open. Keyed on `open` only — the
  // `excludeUserIds` array prop is a fresh reference each parent render, so
  // depending on it here re-fired the effect on every render (request storm).
  useEffect(() => {
    if (!open) return
    setSelected(new Set())
    void loadDirectory()
  }, [open])

  const excludeKey = Array.from(new Set(excludeUserIds.filter(Boolean))).sort().join(',')

  const users = useMemo(() => {
    const exclude = new Set(excludeKey ? excludeKey.split(',') : [])
    return filterCallableContacts(contacts.filter((u) => !exclude.has(u.userId)))
  }, [contacts, excludeKey])

  if (!open) return null

  const chosen = users.filter((u) => selected.has(u.userId))

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-people-title"
    >
      <div className="native-safe-bottom flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="add-people-title" className="text-base font-semibold text-slate-900">
            Add people
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {loading || (loadedAt == null && !error) ? (
            <p className="px-2 py-8 text-center text-sm text-slate-500">Loading contacts…</p>
          ) : error ? (
            <div className="px-2 py-6 text-center">
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="mt-3 text-sm font-semibold text-brand-600 hover:underline"
              >
                Retry
              </button>
            </div>
          ) : users.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-slate-500">No contacts available.</p>
          ) : (
            <ul className="space-y-1" role="list">
              {users.map((user) => {
                const isSelected = selected.has(user.userId)
                return (
                  <li key={user.userId}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelected((prev) => {
                          const next = new Set(prev)
                          if (next.has(user.userId)) next.delete(user.userId)
                          else next.add(user.userId)
                          return next
                        })
                      }}
                      aria-pressed={isSelected}
                      className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                        isSelected
                          ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-200'
                          : 'border-slate-200 hover:border-brand-200 hover:bg-slate-50'
                      }`}
                    >
                      <span className="truncate text-sm font-semibold text-slate-900">
                        {user.displayName}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <div className="border-t border-slate-200 p-4">
          <button
            type="button"
            disabled={chosen.length === 0}
            onClick={() => {
              onAdd(chosen)
              onClose()
            }}
            className="a11y-touch-target w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {chosen.length > 0 ? `Add ${chosen.length} people` : 'Select people to add'}
          </button>
        </div>
      </div>
    </div>
  )
}
